import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { RegisterForm } from '../types';
import { 
  Card, 
  Button, 
  Input, 
  Label, 
  ErrorMessage, 
  LoadingSpinner,
  colors,
  spacing,
  typography
} from '../styles/GlobalStyles';
import { Trophy, Mail, Lock, User, Eye, EyeOff, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const RegisterContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%);
  padding: ${spacing[4]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(0, 255, 65, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(0, 255, 65, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const RegisterCard = styled(Card)`
  width: 100%;
  max-width: 480px;
  padding: ${spacing[10]};
  text-align: center;
  position: relative;
  z-index: 1;
  animation: ${fadeInUp} 0.6s ease-out;
  background: rgba(28, 28, 28, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 255, 65, 0.1);
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.3),
    0 10px 10px -5px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(0, 255, 65, 0.1);

  &:hover {
    border-color: rgba(0, 255, 65, 0.2);
    box-shadow: 
      0 25px 30px -5px rgba(0, 0, 0, 0.4),
      0 15px 15px -5px rgba(0, 0, 0, 0.3),
      0 0 0 1px rgba(0, 255, 65, 0.2);
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[10]};
  color: ${colors.green};
  animation: ${pulse} 2s ease-in-out infinite;
`;

const LogoText = styled.h1`
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  margin: 0;
  background: linear-gradient(135deg, ${colors.green} 0%, ${colors.white} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Title = styled.h2`
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  margin-bottom: ${spacing[3]};
  letter-spacing: -0.025em;
`;

const Subtitle = styled.p`
  color: ${colors.grey[400]};
  margin-bottom: ${spacing[8]};
  font-size: ${typography.fontSize.base};
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  text-align: left;
  animation: ${slideIn} 0.4s ease-out;
  animation-fill-mode: both;

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }
`;

const InputWrapper = styled.div`
  position: relative;
  transition: transform 0.2s ease;

  &:focus-within {
    transform: translateY(-2px);
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${spacing[4]};
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.grey[400]};
  pointer-events: none;
  transition: color 0.2s ease;
  z-index: 2;

  ${InputWrapper}:focus-within & {
    color: ${colors.green};
  }
`;

const StyledInput = styled(Input)`
  padding: ${spacing[4]} ${spacing[4]} ${spacing[4]} 3rem;
  font-size: ${typography.fontSize.base};
  border-radius: ${spacing[3]};
  border: 2px solid ${colors.grey[700]};
  background: rgba(16, 17, 17, 0.8);
  color: ${colors.white};
  transition: all 0.3s ease;
  width: 100%;

  &::placeholder {
    color: ${colors.grey[500]};
    transition: color 0.2s ease;
  }

  &:focus {
    outline: none;
    border-color: ${colors.green};
    background: rgba(16, 17, 17, 0.95);
    box-shadow: 0 0 0 3px rgba(0, 255, 65, 0.1);
  }

  &:focus::placeholder {
    color: ${colors.grey[400]};
  }

  ${props => props.error && `
    border-color: ${colors.error};
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  `}
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${spacing[4]};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${colors.grey[400]};
  cursor: pointer;
  padding: ${spacing[1]};
  border-radius: ${spacing[1]};
  transition: all 0.2s ease;
  z-index: 2;

  &:hover {
    color: ${colors.white};
    background: rgba(255, 255, 255, 0.1);
  }
`;

const PasswordRequirements = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};
  text-align: left;
  margin-top: ${spacing[2]};
  padding: ${spacing[3]};
  background: rgba(16, 17, 17, 0.5);
  border-radius: ${spacing[2]};
  border-left: 3px solid ${colors.grey[600]};
  transition: all 0.3s ease;

  &:hover {
    border-left-color: ${colors.green};
    background: rgba(16, 17, 17, 0.7);
  }
`;

const RequirementItem = styled.div<{ met?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[1]};
  font-size: ${typography.fontSize.xs};
  color: ${props => props.met ? colors.success : colors.grey[400]};
  transition: color 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    flex-shrink: 0;
  }
`;

const RegisterButton = styled(Button)`
  margin-top: ${spacing[4]};
  padding: ${spacing[4]} ${spacing[6]};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  border-radius: ${spacing[3]};
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: ${slideIn} 0.5s ease-out both;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 255, 65, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: ${spacing[8]} 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${colors.grey[700]}, transparent);
  }
  
  &::before {
    margin-right: ${spacing[6]};
  }
  
  &::after {
    margin-left: ${spacing[6]};
  }
`;

const DividerText = styled.span`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
`;

const LoginPrompt = styled.p`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.base};
  margin: 0;
  line-height: 1.6;
`;

const LoginLink = styled(Link)`
  color: ${colors.green};
  text-decoration: none;
  font-weight: ${typography.fontWeight.semibold};
  transition: all 0.2s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${colors.green};
    transition: width 0.2s ease;
  }
  
  &:hover {
    color: ${colors.white};
    
    &::after {
      width: 100%;
    }
  }
`;

const RegisterPage: React.FC = () => {
  const { register: registerUser, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError
  } = useForm<RegisterForm>();

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  // Password validation checks
  const passwordChecks = {
    length: Boolean(password && password.length >= 6),
    match: Boolean(password && confirmPassword && password === confirmPassword)
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (data: RegisterForm) => {
    setIsSubmitting(true);
    try {
      await registerUser(data);
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Registration error:', error);
      
      // Set form errors based on response
      const message = error.response?.data?.message || error.message;
      
      if (message?.includes('username')) {
        setError('username', { message });
      } else if (message?.includes('email')) {
        setError('email', { message });
      } else {
        toast.error(message || 'Registration failed');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <RegisterContainer>
        <LoadingSpinner size="lg" />
      </RegisterContainer>
    );
  }

  return (
    <RegisterContainer>
      <RegisterCard>
        <Logo>
          <Trophy size={40} />
          <LogoText>Fantasy Draft</LogoText>
        </Logo>

        <Title>Create Account</Title>
        <Subtitle>Join the ultimate fantasy football experience and start building your dream team</Subtitle>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <InputGroup>
            <Label htmlFor="username">Username</Label>
            <InputWrapper>
              <InputIcon>
                <User size={20} />
              </InputIcon>
              <StyledInput
                id="username"
                type="text"
                placeholder="Choose a unique username"
                error={!!errors.username}
                {...register('username', {
                  required: 'Username is required',
                  minLength: {
                    value: 3,
                    message: 'Username must be at least 3 characters'
                  },
                  maxLength: {
                    value: 30,
                    message: 'Username must not exceed 30 characters'
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_]+$/,
                    message: 'Username can only contain letters, numbers, and underscores'
                  }
                })}
              />
            </InputWrapper>
            {errors.username && <ErrorMessage>{errors.username.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <Label htmlFor="email">Email Address</Label>
            <InputWrapper>
              <InputIcon>
                <Mail size={20} />
              </InputIcon>
              <StyledInput
                id="email"
                type="email"
                placeholder="Enter your email address"
                error={!!errors.email}
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
            </InputWrapper>
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">Password</Label>
            <InputWrapper>
              <InputIcon>
                <Lock size={20} />
              </InputIcon>
              <StyledInput
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Create a strong password"
                error={!!errors.password}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  },
                  maxLength: {
                    value: 128,
                    message: 'Password must not exceed 128 characters'
                  }
                })}
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </PasswordToggle>
            </InputWrapper>
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
            <PasswordRequirements>
              <RequirementItem met={passwordChecks.length}>
                <CheckCircle size={14} />
                At least 6 characters long
              </RequirementItem>
            </PasswordRequirements>
          </InputGroup>

          <InputGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <InputWrapper>
              <InputIcon>
                <Lock size={20} />
              </InputIcon>
              <StyledInput
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your password"
                error={!!errors.confirmPassword}
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) => 
                    value === password || 'Passwords do not match'
                })}
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </PasswordToggle>
            </InputWrapper>
            {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>}
            {confirmPassword && (
              <PasswordRequirements>
                <RequirementItem met={passwordChecks.match}>
                  <CheckCircle size={14} />
                  Passwords match
                </RequirementItem>
              </PasswordRequirements>
            )}
          </InputGroup>

          <RegisterButton
            type="submit"
            disabled={isSubmitting}
            fullWidth
          >
            {isSubmitting ? <LoadingSpinner size="sm" /> : 'Create Account'}
          </RegisterButton>
        </Form>

        <Divider>
          <DividerText>or</DividerText>
        </Divider>

        <LoginPrompt>
          Already have an account?{' '}
          <LoginLink to="/login">Sign in here</LoginLink>
        </LoginPrompt>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default RegisterPage;
